"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const moment_1 = __importDefault(require("moment"));
const react_1 = __importStar(require("react"));
const dateFormat_1 = require("../../../../types/dateFormat");
const useScreenSizeDetection_1 = __importDefault(require("../../../../utils/useScreenSizeDetection"));
const S = __importStar(require("./TimelapseComponent.style"));
const TimeAxis = ({ currentStep, totalSteps, timestamps, onStepChange, }) => {
    const isMobile = (0, useScreenSizeDetection_1.default)(768);
    const progressBarRef = (0, react_1.useRef)(null);
    const [isDragging, setIsDragging] = (0, react_1.useState)(false);
    const [dragPosition, setDragPosition] = (0, react_1.useState)(null);
    const progressPercentage = (0, react_1.useMemo)(() => {
        const step = isDragging && dragPosition !== null ? dragPosition : currentStep;
        return (step / (totalSteps - 1)) * 100;
    }, [currentStep, totalSteps, isDragging, dragPosition]);
    const calculateStepFromPosition = (0, react_1.useCallback)((clientX) => {
        if (!progressBarRef.current)
            return 0;
        const progressBarRect = progressBarRef.current.getBoundingClientRect();
        const relativePosition = clientX - progressBarRect.left;
        const percentage = (relativePosition / progressBarRect.width) * 100;
        const closestStep = Math.round((percentage / 100) * (totalSteps - 1));
        return Math.max(0, Math.min(totalSteps - 1, closestStep));
    }, [totalSteps]);
    const handleClick = (0, react_1.useCallback)((e) => {
        let clientX;
        if ("touches" in e) {
            clientX = e.touches[0].clientX;
        }
        else {
            clientX = e.clientX;
        }
        const closestStep = calculateStepFromPosition(clientX);
        onStepChange(closestStep);
    }, [calculateStepFromPosition, onStepChange]);
    const handleStart = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        event.target.setPointerCapture(event.pointerId);
        setIsDragging(true);
    }, []);
    const handleMove = (0, react_1.useCallback)((e) => {
        if (!isDragging)
            return;
        e.preventDefault();
        const clientX = e.clientX;
        const closestStep = calculateStepFromPosition(clientX);
        setDragPosition(closestStep);
        onStepChange(closestStep);
    }, [isDragging, calculateStepFromPosition, onStepChange]);
    const handleEnd = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        if (isDragging && dragPosition !== null) {
            onStepChange(dragPosition);
        }
        event.target.releasePointerCapture(event.pointerId);
        setIsDragging(false);
        setDragPosition(null);
    }, [isDragging, dragPosition, onStepChange]);
    const handleTouchStart = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);
    const handleTouchMove = (0, react_1.useCallback)((e) => {
        if (!isDragging)
            return;
        e.preventDefault();
        if (e.touches.length > 0) {
            const clientX = e.touches[0].clientX;
            const closestStep = calculateStepFromPosition(clientX);
            setDragPosition(closestStep);
            onStepChange(closestStep);
        }
    }, [isDragging, calculateStepFromPosition, onStepChange]);
    const handleTouchEnd = (0, react_1.useCallback)(() => {
        if (isDragging && dragPosition !== null) {
            onStepChange(dragPosition);
        }
        setIsDragging(false);
        setDragPosition(null);
    }, [isDragging, dragPosition, onStepChange]);
    (0, react_1.useEffect)(() => {
        if (isDragging) {
            if (isMobile) {
                document.addEventListener("touchmove", handleTouchMove, {
                    passive: false,
                });
                document.addEventListener("touchend", handleTouchEnd);
            }
            else {
                window.addEventListener("pointermove", handleMove, {
                    passive: false,
                });
                window.addEventListener("pointerup", handleEnd);
            }
        }
        else {
            if (isMobile) {
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            }
            else {
                window.removeEventListener("pointermove", handleMove);
                window.removeEventListener("pointerup", handleEnd);
            }
        }
        return () => {
            if (isMobile) {
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            }
            else {
                window.removeEventListener("pointermove", handleMove);
                window.removeEventListener("pointerup", handleEnd);
            }
        };
    }, [
        isDragging,
        isMobile,
        handleTouchMove,
        handleTouchEnd,
        handleMove,
        handleEnd,
    ]);
    const currentTimestamp = timestamps[currentStep];
    const currentDate = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(currentTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.us);
    }, [currentTimestamp]);
    const currentTime = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(currentTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.time);
    }, [currentTimestamp]);
    const firstTimestamp = timestamps[0];
    const lastTimestamp = timestamps[timestamps.length - 1];
    const firstFormattedTime = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(firstTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.time);
    }, [firstTimestamp]);
    const firstFormattedDate = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(firstTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.us_without_year);
    }, [firstTimestamp]);
    const lastFormattedTime = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(lastTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.time);
    }, [lastTimestamp]);
    const lastFormattedDate = (0, react_1.useMemo)(() => {
        return (0, moment_1.default)(lastTimestamp, dateFormat_1.DateFormat.us_timestamp).format(dateFormat_1.DateFormat.us_without_year);
    }, [lastTimestamp]);
    const isStepActive = (index) => {
        const stepPosition = (index / (totalSteps - 1)) * 100;
        const margin = 0.5;
        return progressPercentage >= stepPosition - margin;
    };
    return (react_1.default.createElement(react_1.default.Fragment, null, !isMobile ? (react_1.default.createElement(S.DesktopAxisContainer, null,
        react_1.default.createElement(S.DateContainer, null,
            react_1.default.createElement(S.Time, null, firstFormattedTime),
            react_1.default.createElement(S.Date, null, firstFormattedDate)),
        react_1.default.createElement(S.ProgressBar, { ref: progressBarRef, onMouseDown: handleClick, onTouchStart: handleClick, onPointerDown: handleClick },
            react_1.default.createElement(S.ProgressFiller, { style: { width: `${progressPercentage}%` }, "$isDragging": isDragging }),
            react_1.default.createElement(S.RoundMarker, { "$position": progressPercentage, "$isDragging": isDragging, onPointerDown: handleStart },
                react_1.default.createElement(S.Tooltip, null,
                    react_1.default.createElement("span", null, currentTime),
                    react_1.default.createElement("span", null, currentDate))),
            react_1.default.createElement(S.StepMarkers, null, timestamps.map((_, index) => {
                const position = (index / (totalSteps - 1)) * 100;
                return (react_1.default.createElement(S.StepMarker, { key: index, "$isActive": isStepActive(index), "$isCurrent": index === currentStep, "$isDragging": isDragging, "$position": position }));
            }))),
        react_1.default.createElement(S.DateContainer, null,
            react_1.default.createElement(S.Time, null, lastFormattedTime),
            react_1.default.createElement(S.Date, null, lastFormattedDate)))) : (react_1.default.createElement(S.MobileAxisContainer, null,
        react_1.default.createElement(S.ProgressBar, { ref: progressBarRef, className: "progress-bar", onTouchStart: (e) => {
                e.preventDefault();
                handleClick(e);
            }, onPointerDown: handleStart },
            react_1.default.createElement(S.ProgressFiller, { style: { width: `${progressPercentage}%` }, "$isDragging": isDragging }),
            react_1.default.createElement(S.RoundMarker, { "$position": progressPercentage, onTouchStart: handleTouchStart, onPointerDown: handleStart, "$isDragging": isDragging }),
            react_1.default.createElement(S.StepMarkers, null, timestamps.map((_, index) => {
                const position = (index / (totalSteps - 1)) * 100;
                return (react_1.default.createElement(S.StepMarker, { key: index, "$isActive": isStepActive(index), "$isCurrent": index === currentStep, "$position": position, "$isDragging": isDragging }));
            })))))));
};
exports.default = TimeAxis;
